import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestUrl } from "@tanstack/react-start/server";

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  type Locale,
  LOCALE_PREFIX,
  localeFromAcceptLanguage,
  localeFromPathname,
  normaliseLocale,
} from "./config";

function readCookie(cookieHeader: string | null | undefined, name: string): string | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

/**
 * Resolves the active locale identically on the server (from the request URL
 * prefix, then cookie, then Accept-Language) and on the client (from the
 * current pathname prefix, then cookie, then navigator languages). Keeping the
 * two in sync is what prevents a hydration mismatch.
 */
export const detectLocale = createIsomorphicFn()
  .server((): Locale => {
    try {
      const url = getRequestUrl();
      const fromPath = localeFromPathname(url.pathname);
      if (fromPath) return fromPath;
      const cookie = normaliseLocale(readCookie(getRequestHeader("cookie"), LOCALE_COOKIE));
      if (cookie) return cookie;
      const accept = localeFromAcceptLanguage(getRequestHeader("accept-language"));
      if (accept) return accept;
    } catch {
      /* no request context (build / prerender) */
    }
    return DEFAULT_LOCALE;
  })
  .client((): Locale => {
    const fromPath = localeFromPathname(window.location.pathname);
    if (fromPath) return fromPath;
    const cookie = normaliseLocale(readCookie(document.cookie, LOCALE_COOKIE));
    if (cookie) return cookie;
    for (const lang of navigator.languages ?? [navigator.language]) {
      const l = normaliseLocale(lang);
      if (l) return l;
    }
    return DEFAULT_LOCALE;
  });

/** Router basepath for the active locale ("" for English). */
export function localeBasepath(locale: Locale): string {
  return LOCALE_PREFIX[locale] || "/";
}

export function persistLocaleCookie(locale: Locale) {
  if (typeof document === "undefined") return;
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=31536000; samesite=lax`;
}
